import { useEffect, useState } from "react";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

import Drawer from "../../Drawer";
import MethodSelectList from "../deposit/MethodSelectList";
import BankTransferPanel from "../deposit/BankTransferPanel";
import ComingSoonPanel from "../deposit/ComingSoonPanel";
import {
  FundingMethodId,
  getFundingMethod,
  getFundingMethodsForCurrency,
  isMethodAvailable,
} from "../../../utils/fundingMethods";

export interface Props {
  isOpen: boolean;
  currency: string | null;
  walletId: string | null;

  onClose: () => void;
  buildApiUrl: (url: string) => string;
}

type Screen = "select" | FundingMethodId;

const slideVariants: Variants = {
  enter: (dir: number) => ({ x: dir > 0 ? 48 : -48, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -48 : 48, opacity: 0 }),
};

export default function WalletDepositDrawer({
  isOpen,
  onClose,
  buildApiUrl,
  currency,
}: Props) {
  const [screen, setScreen] = useState<Screen>("select");
  const [direction, setDirection] = useState(1);

  const methods = getFundingMethodsForCurrency(currency);
  const activeMethod =
    screen !== "select" ? getFundingMethod(screen) : undefined;
  const isActiveMethodAvailable = activeMethod
    ? isMethodAvailable(activeMethod, currency)
    : false;

  // Always start on the method picker each time the drawer opens or the
  // target wallet changes.
  useEffect(() => {
    setScreen("select");
    setDirection(1);
  }, [isOpen, currency]);

  const openMethod = (id: FundingMethodId) => {
    setDirection(1);
    setScreen(id);
  };

  const goBack = () => {
    setDirection(-1);
    setScreen("select");
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={activeMethod ? activeMethod.label : "Add money"}
    >
      {activeMethod && (
        <button
          type="button"
          onClick={goBack}
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-primary"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          All methods
        </button>
      )}

      <div className="relative overflow-hidden">
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={screen}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
          >
            {screen === "select" && (
              <>
                <p className="mb-4 text-sm text-gray-600">
                  Fund your <span className="font-medium">{currency}</span>{" "}
                  wallet
                </p>
                <MethodSelectList
                  methods={methods}
                  currency={currency}
                  onSelect={openMethod}
                />
              </>
            )}

            {/* A method that isn't live for this currency always shows the
                coming-soon placeholder, regardless of which method it is. */}
            {activeMethod && !isActiveMethodAvailable && (
              <ComingSoonPanel method={activeMethod} />
            )}

            {isActiveMethodAvailable && screen === "bank-transfer" && (
              <BankTransferPanel
                currency={currency}
                buildApiUrl={buildApiUrl}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </Drawer>
  );
}
